import React, { useState, useEffect, useCallback } from 'react';
import { format } from 'date-fns';
import {
  ChevronRight,
  Description,
  Folder,
  GridView,
  List,
  MoreVert,
  Add,
  Search,
  Upload,
  TextSnippet,
  PictureAsPdf,
  Image,
  Code,
  Download,
  Delete,
  Edit,
  DriveFileMove,
  Visibility,
  Close,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Button,
  TextField,
  Checkbox,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  Divider,
  Tabs,
  Tab,
  LinearProgress,
  Chip,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Snackbar,
  CircularProgress,
} from '@mui/material';
import { debounce } from 'lodash';
import {
  getFiles,
  uploadFile,
  createFolder,
  // renameFile,
  // deleteFiles,
  moveFiles,
  downloadFile,
  downloadFolder,
  previewFile,
} from './api';

// Helper function to format dates
const formatDate = (date) => {
  const parsedDate = new Date(date);
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  if (parsedDate.toDateString() === now.toDateString()) {
    return 'Today';
  } else if (parsedDate.toDateString() === yesterday.toDateString()) {
    return 'Yesterday';
  } else {
    return format(parsedDate, 'MMM d, yyyy');
  }
};

// Helper function to get file icon
const getFileIcon = (type) => {
  switch (type) {
    case 'folder':
      return <Folder sx={{ fontSize: 20, color: 'blue' }} />;
    case 'pdf':
      return <PictureAsPdf sx={{ fontSize: 20, color: 'red' }} />;
    case 'image':
      return <Image sx={{ fontSize: 20, color: 'green' }} />;
    case 'document':
      return <Description sx={{ fontSize: 20, color: '#b8860b' }} />;
    case 'code':
      return <Code sx={{ fontSize: 20, color: 'purple' }} />;
    default:
      return <TextSnippet sx={{ fontSize: 20, color: 'grey' }} />;
  }
};

export default function FileManager() {
  const theme = useTheme();
  const backgroundDefault = theme.palette.background.default;
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [files, setFiles] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [viewMode, setViewMode] = useState('list');
  const [currentPath, setCurrentPath] = useState(['Home']);
  const [searchQuery, setSearchQuery] = useState('');
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [newFolderModalOpen, setNewFolderModalOpen] = useState(false);
  const [renameModalOpen, setRenameModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [moveModalOpen, setMoveModalOpen] = useState(false);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [activeFile, setActiveFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadingFile, setUploadingFile] = useState('');
  const [newFolderName, setNewFolderName] = useState('');
  const [newFileName, setNewFileName] = useState('');
  const [selectedFolderPath, setSelectedFolderPath] = useState([]);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuFile, setMenuFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Debounced search
  const debouncedSearch = useCallback(() =>
    debounce((value) => setSearchQuery(value), 300)
  , []);

  useEffect(() => {
    const fetchFiles = async () => {
      setLoading(true);
      try {
        const response = await getFiles(JSON.stringify(currentPath), searchQuery);
        setFiles(response.data);
      } catch (error) {
        setError(error.response?.data?.error || 'Failed to fetch files');
      } finally {
        setLoading(false);
      }
    };
    fetchFiles();
  }, [currentPath, searchQuery]);

  const toggleSelection = (id) => {
    setSelectedFiles((prev) => (prev.includes(id) ? prev.filter((fileId) => fileId !== id) : [...prev, id]));
  };

  const toggleSelectAll = () => {
    if (selectedFiles.length === filteredFiles.length) {
      setSelectedFiles([]);
    } else {
      setSelectedFiles(filteredFiles.map((file) => file.id));
    }
  };

  const navigateToFolder = (folder) => {
    if (folder.type === 'folder') {
      setCurrentPath([...folder.path, folder.name]);
      setSelectedFiles([]);
    }
  };

  const navigateToBreadcrumb = (index) => {
    setCurrentPath(currentPath.slice(0, index + 1));
    setSelectedFiles([]);
  };

  const handleFileAction = (action, file) => {
    setActiveFile(file);
    switch (action) {
      case 'open':
        if (file.type === 'folder') {
          navigateToFolder(file);
        } else {
          handlePreview(file);
        }
        break;
      case 'download':
        handleDownload(file);
        break;
      case 'rename':
        setNewFileName(file.name);
        setRenameModalOpen(true);
        break;
      case 'move':
        setMoveModalOpen(true);
        break;
      case 'delete':
        setDeleteModalOpen(true);
        break;
      case 'preview':
        handlePreview(file);
        break;
      default:
        break;
    }
  };

  const handleBulkAction = (action) => {
    switch (action) {
      case 'download':
        if (selectedFiles.length === 1) {
          const file = files.find((f) => f.id === selectedFiles[0]);
          handleDownload(file);
        } else {
          setError('Please select exactly one file for download');
        }
        break;
      case 'move':
        setMoveModalOpen(true);
        break;
      case 'delete':
        setDeleteModalOpen(true);
        break;
      default:
        break;
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    setUploadingFile(file.name);
    setUploadProgress(0);
    try {
      await uploadFile(file, currentPath, {
        onUploadProgress: (progress) => setUploadProgress(progress),
      });
      const response = await getFiles(JSON.stringify(currentPath), searchQuery);
      setFiles(response.data);
      setUploadModalOpen(false);
    } catch (error) {
      setError(error.response?.data?.error || 'Upload failed');
    } finally {
      setUploadingFile('');
      setUploadProgress(0);
    }
  };

  const createNewFolder = async () => {
    if (newFolderName.trim() === '') {
      setError('Folder name cannot be empty');
      return;
    }
    try {
      await createFolder(newFolderName, JSON.stringify(currentPath));
      const response = await getFiles(JSON.stringify(currentPath), searchQuery);
      setFiles(response.data);
      setNewFolderName('');
      setNewFolderModalOpen(false);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to create folder');
    }
  };

  const renameFile = async () => {
    if (!activeFile || newFileName.trim() === '') {
      setError('File name cannot be empty');
      return;
    }
    try {
      await renameFile(activeFile.id, newFileName);
      const response = await getFiles(JSON.stringify(currentPath), searchQuery);
      setFiles(response.data);
      setRenameModalOpen(false);
      setActiveFile(null);
    } catch (error) {
      setError(error.response?.data?.error || 'Rename failed');
    }
  };

  const deleteFiles = async () => {
    try {
      const ids = selectedFiles.length > 0 ? selectedFiles : [activeFile?.id];
      await deleteFiles(ids);
      const response = await getFiles(JSON.stringify(currentPath), searchQuery);
      setFiles(response.data);
      setSelectedFiles([]);
      setActiveFile(null);
      setDeleteModalOpen(false);
    } catch (error) {
      setError(error.response?.data?.error || 'Delete failed');
    }
  };

  const handleMove = async () => {
    try {
      const ids = selectedFiles.length > 0 ? selectedFiles : [activeFile.id];
      await moveFiles(ids, JSON.stringify(selectedFolderPath));
      const response = await getFiles(JSON.stringify(currentPath), searchQuery);
      setFiles(response.data);
      setSelectedFiles([]);
      setActiveFile(null);
      setMoveModalOpen(false);
      setSelectedFolderPath([]);
    } catch (error) {
      setError(error.response?.data?.error || 'Move failed');
    }
  };

  const handleDownload = async (file) => {
    try {
      const response = file.type === 'folder' ? await downloadFolder(file.id) : await downloadFile(file.id);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', file.type === 'folder' ? `${file.name}.zip` : file.name);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      setError(error.response?.data?.error || 'Download failed');
    }
  };

  const handlePreview = async (file) => {
    try {
      const response = await previewFile(file.id);
      const url = window.URL.createObjectURL(new Blob([response.data], { type: response.headers['content-type'] }));
      setPreviewUrl(url);
      setPreviewModalOpen(true);
    } catch (error) {
      setError(error.response?.data?.error || 'Preview failed');
    }
  };

  const handleMenuOpen = (event, file) => {
    setAnchorEl(event.currentTarget);
    setMenuFile(file);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setMenuFile(null);
  };

  const filteredFiles = (files ?? []).filter((file) => {
    const pathMatch = JSON.stringify(file.path) === JSON.stringify(currentPath);
    const searchMatch =
      searchQuery === '' || file.name.toLowerCase().includes(searchQuery.toLowerCase());
    return pathMatch && searchMatch;
  });


  return (
    <Box className="main-container-box" bgcolor={backgroundDefault}>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        message={error}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      />
      <Card sx={{ position: 'relative', maxWidth: 1200, mx: 'auto' }}>
        <CardHeader
          sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}
          title={
            <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} alignItems="center" justifyContent="space-between" gap={2}>
              <Box display="flex" alignItems="center" flexWrap="wrap" gap={1} color="text.secondary" fontSize="0.875rem">
                {currentPath.map((path, index) => (
                  <Box key={index} display="flex" alignItems="center">
                    {index > 0 && <ChevronRight sx={{ fontSize: 16, mx: 0.5 }} />}
                    <Button
                      variant="text"
                      onClick={() => navigateToBreadcrumb(index)}
                      sx={{
                        fontSize: '0.875rem',
                        textTransform: 'none',
                        color: index === currentPath.length - 1 ? 'primary.main' : 'text.secondary',
                        fontWeight: index === currentPath.length - 1 ? 'medium' : 'normal',
                        '&:hover': { color: 'primary.main' },
                      }}
                    >
                      {path}
                    </Button>
                  </Box>
                ))}
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <TextField
                  size="small"
                  placeholder="Search files..."
                  onChange={(e) => debouncedSearch(e.target.value)}
                  InputProps={{
                    startAdornment: <Search sx={{ fontSize: 20, color: 'text.secondary', mr: 1 }} />,
                    sx: { width: { xs: '100%', md: 180, lg: 250 } },
                  }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setNewFolderModalOpen(true)}
                  startIcon={<Add />}
                  sx={{ textTransform: 'none' }}
                >
                  New Folder
                </Button>
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => setUploadModalOpen(true)}
                  startIcon={<Upload />}
                  sx={{ textTransform: 'none' }}
                >
                  Upload
                </Button>
                {!isMobile && (
                  <Box display="flex" border={`1px solid ${theme.palette.divider}`} borderRadius={1}>
                    <IconButton
                      color={viewMode === 'list' ? 'primary' : 'default'}
                      onClick={() => setViewMode('list')}
                      sx={{ borderRadius: '4px 0 0 4px' }}
                    >
                      <List />
                    </IconButton>
                    <IconButton
                      color={viewMode === 'grid' ? 'primary' : 'default'}
                      onClick={() => setViewMode('grid')}
                      sx={{ borderRadius: '0 4px 4px 0' }}
                    >
                      <GridView />
                    </IconButton>
                  </Box>
                )}
              </Box>
            </Box>
          }
        />
        {selectedFiles.length > 0 && (
          <Box sx={{ bgcolor: 'background.default', p: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Button variant="text" size="small" onClick={() => setSelectedFiles([])} startIcon={<Close />}>
                Deselect
              </Button>
              <Typography variant="body2" color="text.secondary">
                {selectedFiles.length} item{selectedFiles.length !== 1 ? 's' : ''} selected
              </Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Button variant="outlined" size="small" onClick={() => handleBulkAction('download')} startIcon={<Download />}>
                Download
              </Button>
              <Button variant="outlined" size="small" onClick={() => handleBulkAction('move')} startIcon={<DriveFileMove />}>
                Move
              </Button>
              <Button
                variant="contained"
                color="error"
                size="small"
                onClick={() => handleBulkAction('delete')}
                startIcon={<Delete />}
              >
                Delete
              </Button>
            </Box>
          </Box>
        )}
        <CardContent sx={{ p: 0 }}>
          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <>
              <Tabs
                value={viewMode}
                onChange={(_, value) => setViewMode(value)}
                sx={{ display: isMobile ? 'none' : 'block' }}
              >
                <Tab value="list" label="List" />
                <Tab value="grid" label="Grid" />
              </Tabs>
              {viewMode === 'list' && (
                <Box>
                  {filteredFiles.length > 0 ? (
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: 40 }}>
                            <Checkbox
                              checked={selectedFiles.length === filteredFiles.length && filteredFiles.length > 0}
                              onChange={toggleSelectAll}
                            />
                          </TableCell>
                          <TableCell sx={{ width: 300 }}>Name</TableCell>
                          <TableCell>Size</TableCell>
                          <TableCell>Modified</TableCell>
                          <TableCell sx={{ width: 80 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filteredFiles.map((file) => (
                          <TableRow
                            key={file.id}
                            sx={{ bgcolor: selectedFiles.includes(file.id) ? 'action.selected' : 'inherit' }}
                          >
                            <TableCell>
                              <Checkbox checked={selectedFiles.includes(file.id)} onChange={() => toggleSelection(file.id)} />
                            </TableCell>
                            <TableCell>
                              <Box
                                display="flex"
                                alignItems="center"
                                gap={1}
                                sx={{ cursor: 'pointer' }}
                                onClick={() => handleFileAction('open', file)}
                              >
                                {getFileIcon(file.type)}
                                <Typography variant="body2" fontWeight="medium">
                                  {file.name}
                                </Typography>
                              </Box>
                            </TableCell>
                            <TableCell>
                              {file.type === 'folder' ? `${file.items} item${file.items !== 1 ? 's' : ''}` : file.size}
                            </TableCell>
                            <TableCell>{formatDate(file.updatedAt)}</TableCell>
                            <TableCell>
                              <IconButton onClick={(e) => handleMenuOpen(e, file)}>
                                <MoreVert />
                              </IconButton>
                              <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl) && menuFile?.id === file.id}
                                onClose={handleMenuClose}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                              >
                                <MenuItem onClick={() => { handleFileAction('open', file); handleMenuClose(); }}>
                                  {file.type === 'folder' ? (
                                    <>
                                      <Folder sx={{ mr: 1 }} />
                                      Open
                                    </>
                                  ) : (
                                    <>
                                      <Visibility sx={{ mr: 1 }} />
                                      Preview
                                    </>
                                  )}
                                </MenuItem>
                                {file.type !== 'folder' && (
                                  <MenuItem onClick={() => { handleFileAction('download', file); handleMenuClose(); }}>
                                    <Download sx={{ mr: 1 }} />
                                    Download
                                  </MenuItem>
                                )}
                                {file.type === 'folder' && (
                                  <MenuItem onClick={() => { handleFileAction('download', file); handleMenuClose(); }}>
                                    <Download sx={{ mr: 1 }} />
                                    Download as ZIP
                                  </MenuItem>
                                )}
                                <Divider />
                                <MenuItem onClick={() => { handleFileAction('rename', file); handleMenuClose(); }}>
                                  <Edit sx={{ mr: 1 }} />
                                  Rename
                                </MenuItem>
                                <MenuItem onClick={() => { handleFileAction('move', file); handleMenuClose(); }}>
                                  <DriveFileMove sx={{ mr: 1 }} />
                                  Move
                                </MenuItem>
                                <Divider />
                                <MenuItem
                                  onClick={() => { handleFileAction('delete', file); handleMenuClose(); }}
                                  sx={{ color: 'error.main' }}
                                >
                                  <Delete sx={{ mr: 1 }} />
                                  Delete
                                </MenuItem>
                              </Menu>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  ) : (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={12}>
                      <Box bgcolor="action.disabledBackground" p={2} borderRadius="50%">
                        <Folder sx={{ fontSize: 24, color: 'text.secondary' }} />
                      </Box>
                      <Typography variant="h6" mt={2}>
                        No files found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={400} mt={1}>
                        {searchQuery
                          ? `No results found for "${searchQuery}". Try a different search term.`
                          : 'This folder is empty. Upload a file or create a new folder.'}
                      </Typography>
                      <Box display="flex" gap={1} mt={2}>
                        <Button variant="outlined" size="small" onClick={() => setNewFolderModalOpen(true)} startIcon={<Add />}>
                          New Folder
                        </Button>
                        <Button variant="contained" size="small" onClick={() => setUploadModalOpen(true)} startIcon={<Upload />}>
                          Upload File
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
              {viewMode === 'grid' && (
                <Box>
                  {filteredFiles.length > 0 ? (
                    <Box
                      display="grid"
                      gridTemplateColumns={{
                        xs: 'repeat(2, 1fr)',
                        sm: 'repeat(3, 1fr)',
                        md: 'repeat(4, 1fr)',
                        lg: 'repeat(5, 1fr)',
                      }}
                      gap={2}
                      p={2}
                    >
                      {filteredFiles.map((file) => (
                        <Box
                          key={file.id}
                          sx={{
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            p: 1,
                            transition: 'all 0.2s',
                            '&:hover': { boxShadow: 3 },
                            position: 'relative',
                            ...(selectedFiles.includes(file.id) && { borderColor: 'primary.main', borderWidth: 2 }),
                          }}
                        >
                          <Box position="absolute" top={8} right={8}>
                            <Checkbox checked={selectedFiles.includes(file.id)} onChange={() => toggleSelection(file.id)} />
                          </Box>
                          <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="center"
                            p={2}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => handleFileAction('open', file)}
                          >
                            <Box fontSize={40}>{getFileIcon(file.type)}</Box>
                            <Typography variant="body2" fontWeight="medium" textAlign="center" noWrap maxWidth={120} mt={1}>
                              {file.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" textAlign="center">
                              {file.type === 'folder' ? `${file.items} item${file.items !== 1 ? 's' : ''}` : file.size}
                            </Typography>
                          </Box>
                          <Box position="absolute" bottom={8} right={8}>
                            <IconButton
                              onClick={(e) => handleMenuOpen(e, file)}
                              sx={{ opacity: { xs: 1, md: 0 }, '&:hover': { opacity: 1 } }}
                            >
                              <MoreVert />
                            </IconButton>
                            <Menu
                              anchorEl={anchorEl}
                              open={Boolean(anchorEl) && menuFile?.id === file.id}
                              onClose={handleMenuClose}
                              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            >
                              <MenuItem onClick={() => { handleFileAction('open', file); handleMenuClose(); }}>
                                {file.type === 'folder' ? (
                                  <>
                                    <Folder sx={{ mr: 1 }} />
                                    Open
                                  </>
                                ) : (
                                  <>
                                    <Visibility sx={{ mr: 1 }} />
                                    Preview
                                  </>
                                )}
                              </MenuItem>
                              {file.type !== 'folder' && (
                                <MenuItem onClick={() => { handleFileAction('download', file); handleMenuClose(); }}>
                                  <Download sx={{ mr: 1 }} />
                                  Download
                                </MenuItem>
                              )}
                              {file.type === 'folder' && (
                                <MenuItem onClick={() => { handleFileAction('download', file); handleMenuClose(); }}>
                                  <Download sx={{ mr: 1 }} />
                                  Download as ZIP
                                </MenuItem>
                              )}
                              <Divider />
                              <MenuItem onClick={() => { handleFileAction('rename', file); handleMenuClose(); }}>
                                <Edit sx={{ mr: 1 }} />
                                Rename
                              </MenuItem>
                              <MenuItem onClick={() => { handleFileAction('move', file); handleMenuClose(); }}>
                                <DriveFileMove sx={{ mr: 1 }} />
                                Move
                              </MenuItem>
                              <Divider />
                              <MenuItem
                                onClick={() => { handleFileAction('delete', file); handleMenuClose(); }}
                                sx={{ color: 'error.main' }}
                              >
                                <Delete sx={{ mr: 1 }} />
                                Delete
                              </MenuItem>
                            </Menu>
                          </Box>
                        </Box>
                      ))}
                    </Box>
                  ) : (
                    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={12}>
                      <Box bgcolor="action.disabledBackground" p={2} borderRadius="50%">
                        <Folder sx={{ fontSize: 24, color: 'text.secondary' }} />
                      </Box>
                      <Typography variant="h6" mt={2}>
                        No files found
                      </Typography>
                      <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={400} mt={1}>
                        {searchQuery
                          ? `No results found for "${searchQuery}". Try a different search term.`
                          : 'This folder is empty. Upload a file or create a new folder.'}
                      </Typography>
                      <Box display="flex" gap={1} mt={2}>
                        <Button variant="outlined" size="small" onClick={() => setNewFolderModalOpen(true)} startIcon={<Add />}>
                          New Folder
                        </Button>
                        <Button variant="contained" size="small" onClick={() => setUploadModalOpen(true)} startIcon={<Upload />}>
                          Upload File
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Box>
              )}
            </>
          )}
        </CardContent>

        <Dialog open={uploadModalOpen} onClose={() => setUploadModalOpen(false)}>
          <DialogTitle>Upload File</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Drag and drop a file or click to browse.
            </Typography>
            <Box
              sx={{
                border: `2px dashed ${theme.palette.divider}`,
                borderRadius: 2,
                p: 6,
                textAlign: 'center',
                cursor: 'pointer',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <input
                type="file"
                onChange={handleUpload}
                style={{ display: 'none' }}
                id="file-upload"
              />
              <label htmlFor="file-upload">
                <Box display="flex" flexDirection="column" alignItems="center">
                  <Upload sx={{ fontSize: 40, color: 'text.secondary', mb: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Drag and drop your file here or click to browse
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Supports PDF, DOCX, JPG, PNG, and more
                  </Typography>
                </Box>
              </label>
            </Box>
            {uploadingFile && (
              <Box mt={2}>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">{uploadingFile}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {uploadProgress}%
                  </Typography>
                </Box>
                <LinearProgress variant="determinate" value={uploadProgress} />
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setUploadModalOpen(false)}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={newFolderModalOpen} onClose={() => setNewFolderModalOpen(false)}>
          <DialogTitle>Create New Folder</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enter a name for your new folder.
            </Typography>
            <TextField
              fullWidth
              placeholder="Folder Name"
              value={newFolderName}
              onChange={(e) => setNewFolderName(e.target.value)}
              size="small"
            />
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setNewFolderModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={createNewFolder}>
              Create Folder
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={renameModalOpen} onClose={() => setRenameModalOpen(false)}>
          <DialogTitle>Rename {activeFile?.type === 'folder' ? 'Folder' : 'File'}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Enter a new name for your {activeFile?.type === 'folder' ? 'folder' : 'file'}.
            </Typography>
            <TextField
              fullWidth
              placeholder={activeFile?.type === 'folder' ? 'Folder Name' : 'File Name'}
              value={newFileName}
              onChange={(e) => setNewFileName(e.target.value)}
              size="small"
            />
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setRenameModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" onClick={renameFile}>
              Rename
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)}>
          <DialogTitle>Confirm Deletion</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary">
              {selectedFiles.length > 0
                ? `Are you sure you want to delete ${selectedFiles.length} selected item${selectedFiles.length !== 1 ? 's' : ''}?`
                : `Are you sure you want to delete "${activeFile?.name}"?`}
              This action cannot be undone.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setDeleteModalOpen(false)}>
              Cancel
            </Button>
            <Button variant="contained" color="error" onClick={deleteFiles}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={moveModalOpen} onClose={() => setMoveModalOpen(false)}>
          <DialogTitle>Move {selectedFiles.length > 0 ? 'Items' : activeFile?.name}</DialogTitle>
          <DialogContent>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Select a destination folder.
            </Typography>
            <Box sx={{ maxHeight: 200, overflowY: 'auto', border: `1px solid ${theme.palette.divider}`, borderRadius: 1, p: 2 }}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                p={1}
                sx={{
                  '&:hover': { bgcolor: 'action.hover' },
                  cursor: 'pointer',
                  borderRadius: 1,
                  bgcolor: JSON.stringify(selectedFolderPath) === JSON.stringify(['Home']) ? 'action.selected' : 'inherit',
                }}
                onClick={() => setSelectedFolderPath(['Home'])}
              >
                <Folder sx={{ fontSize: 20, color: 'blue' }} />
                <Typography variant="body2">Home</Typography>
              </Box>
              {files
                .filter((file) => file.type === 'folder' && JSON.stringify(file.path) === JSON.stringify(currentPath))
                .map((folder) => (
                  <Box
                    key={folder.id}
                    display="flex"
                    alignItems="center"
                    gap={1}
                    p={1}
                    sx={{
                      '&:hover': { bgcolor: 'action.hover' },
                      cursor: 'pointer',
                      borderRadius: 1,
                      bgcolor: JSON.stringify(selectedFolderPath) === JSON.stringify([...folder.path, folder.name]) ? 'action.selected' : 'inherit',
                    }}
                    onClick={() => setSelectedFolderPath([...folder.path, folder.name])}
                  >
                    <Folder sx={{ fontSize: 20, color: 'blue' }} />
                    <Typography variant="body2">{folder.name}</Typography>
                  </Box>
                ))}
            </Box>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={() => setMoveModalOpen(false)}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleMove}
              disabled={selectedFolderPath.length === 0}
            >
              Move Here
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={previewModalOpen} onClose={() => { setPreviewModalOpen(false); setPreviewUrl(''); }} maxWidth="sm" fullWidth>
          <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
            {getFileIcon(activeFile?.type || 'other')}
            <Typography variant="h6" ml={1}>
              {activeFile?.name}
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight={300}>
              {activeFile?.type === 'image' ? (
                <img src={previewUrl} alt={activeFile?.name} style={{ maxWidth: '100%', maxHeight: 300 }} />
              ) : activeFile?.type === 'pdf' ? (
                <iframe src={previewUrl} style={{ width: '100%', height: 300 }} title={activeFile?.name} />
              ) : (
                <Box bgcolor="action.disabledBackground" width="100%" height={300} borderRadius={1} display="flex" alignItems="center" justifyContent="center">
                  <Description sx={{ fontSize: 64, color: 'text.secondary' }} />
                </Box>
              )}
            </Box>
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={2}>
              <Box display="flex" gap={1}>
                <Chip label={activeFile?.size || 'Unknown size'} variant="outlined" size="small" />
                <Chip label={formatDate(activeFile?.updatedAt || new Date())} variant="outlined" size="small" />
              </Box>
              <Button variant="contained" onClick={() => { setPreviewModalOpen(false); setPreviewUrl(''); }}>
                Close
              </Button>
            </Box>
          </DialogContent>
        </Dialog>
      </Card>
    </Box>
  );
}